function clean(value) {
  return `${value || ""}`.trim();
}

function splitClientName(fullName) {
  const parts = clean(fullName).split(/\s+/).filter(Boolean);
  return {
    firstName: parts.shift() || "",
    surname: parts.join(" "),
  };
}

export function buildCounselListEmail(appearance, clerkEmail) {
  const recipient = clean(clerkEmail);
  if (!recipient || !recipient.includes("@")) {
    throw new Error("Enter the clerk's email address.");
  }

  const clientName = clean(appearance?.clientName) || "the client";
  const matterReference = clean(appearance?.fileNumber);
  const appearanceType = clean(appearance?.appearanceType) || "court appearance";
  const date = clean(appearance?.date) || "the listed date";
  const court = clean(appearance?.court) || "the listed court";
  const location = clean(appearance?.listingLocation);

  return {
    toRecipients: [recipient],
    subject: `Request for available counsel - ${clientName} - ${date}`,
    body: [
      "Dear Clerks,",
      "",
      `Could you please provide a list of available counsel for the following ${appearanceType.toLowerCase()}:`,
      "",
      `Client: ${clientName}`,
      matterReference ? `Matter: ${matterReference}` : "",
      `Date: ${date}`,
      `Court: ${court}`,
      location ? `Listed at: ${location}` : "",
      appearance?.fundingType ? `Funding: ${clean(appearance.fundingType)}` : "",
      appearance?.fundingType === "Legal Aid" && appearance?.grantOfAidNumber
        ? `Grant of aid: ${clean(appearance.grantOfAidNumber)}`
        : "",
      "",
      "Please let me know which barristers are available and their applicable fees.",
      "",
      "Kind regards,",
    ]
      .filter((line, index, lines) => line !== "" || lines[index - 1] !== "")
      .join("\n"),
  };
}

export function buildLacwBacksheetUrl(origin, appearance) {
  const url = new URL("/lacw-backsheet", origin);
  const { firstName, surname } = splitClientName(appearance?.clientName);
  const params = {
    source: "outlook",
    clientFirstName: firstName,
    clientSurname: surname,
    caseId: clean(appearance?.fileNumber),
    court: clean(appearance?.court),
    listingLocation: clean(appearance?.listingLocation),
    hearingDate: clean(appearance?.date),
    hearingTime: clean(appearance?.startTime),
    appearanceType: clean(appearance?.appearanceType),
    fundingType: clean(appearance?.fundingType),
    grantNumber: clean(appearance?.grantOfAidNumber),
  };

  Object.entries(params).forEach(([key, value]) => {
    if (value) url.searchParams.set(key, value);
  });
  return url.toString();
}

export function readOutlookBacksheetPrefill(search) {
  const params = new URLSearchParams(search);
  if (params.get("source") !== "outlook") return null;

  const court = params.get("court") || "";
  const courtLower = court.toLowerCase();
  let courtName = "Other";
  if (courtLower.includes("magistrates")) courtName = "Magistrates Court";
  else if (courtLower.includes("county")) courtName = "County Court";
  else if (courtLower.includes("supreme")) courtName = "Supreme Court";
  else if (courtLower.includes("children")) courtName = "Children's Court (Summary)";
  else if (courtLower.includes("appeal")) courtName = "Court of appeal";

  const fundingType = params.get("fundingType") || "";
  const grantNumber = params.get("grantNumber") || "";
  const listingLocation = params.get("listingLocation") || "";
  const additionalNotes = [
    "Opened from CLS Connect in Outlook.",
    listingLocation ? `Listed at: ${listingLocation}` : "",
    fundingType ? `Funding type: ${fundingType}` : "",
    grantNumber ? `Grant of aid number: ${grantNumber}` : "",
  ]
    .filter(Boolean)
    .join("\n");

  return {
    client_first_name: params.get("clientFirstName") || "",
    client_surname: params.get("clientSurname") || "",
    case_id: params.get("caseId") || "",
    court_name: court ? courtName : "",
    court_name_other: courtName === "Other" ? court : "",
    hearing_date: params.get("hearingDate") || "",
    hearing_time: params.get("hearingTime") || "",
    appearance_type: params.get("appearanceType") || "",
    aid_status: fundingType === "Legal Aid" ? "approved" : "",
    additional_notes: additionalNotes,
  };
}

export function openCounselListEmail(draft, Office = window.Office) {
  const displayNewMessageForm = Office?.context?.mailbox?.displayNewMessageForm;
  if (typeof displayNewMessageForm === "function") {
    displayNewMessageForm.call(Office.context.mailbox, {
      toRecipients: draft.toRecipients,
      subject: draft.subject,
      htmlBody: draft.body.replace(/\n/g, "<br>"),
    });
    return { mock: false };
  }

  const to = encodeURIComponent(draft.toRecipients.join(";"));
  window.location.href = `mailto:${to}?subject=${encodeURIComponent(draft.subject)}&body=${encodeURIComponent(draft.body)}`;
  return { mock: true };
}

export function openPortalWindow(url, Office = window.Office) {
  const openBrowserWindow = Office?.context?.ui?.openBrowserWindow;
  if (typeof openBrowserWindow === "function") {
    openBrowserWindow.call(Office.context.ui, url);
    return { mock: false };
  }

  window.open(url, "_blank", "noopener,noreferrer");
  return { mock: true };
}
